import { Component, type ErrorInfo, type PropsWithChildren } from 'react';

import { isApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';

import {
  FullPageErrorFallback,
  InlineErrorFallback,
  MobileErrorFallback,
  NoneErrorFallback,
  PartialErrorFallback,
} from '../error-fallbacks';

interface ErrorBoundaryProps extends PropsWithChildren {
  fallbackType?: FallbackType;
  feature?: string;
  owner?: string;
}
interface ErrorBoundaryState {
  hasError: boolean;
}

export const FALLBACK_MAP = {
  full: FullPageErrorFallback,
  partial: PartialErrorFallback,
  inline: InlineErrorFallback,
  mobile: MobileErrorFallback,
  none: NoneErrorFallback,
};

export type FallbackType = keyof typeof FALLBACK_MAP;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isApolloError(error)) return;

    const { feature, owner } = this.props;

    Sentry.withScope(scope => {
      if (feature) scope.setTag('feature', feature);
      if (owner) scope.setTag('owner', owner);
      scope.setExtras({ errorInfo });
      Sentry.captureException(error);
    });
  }

  render() {
    const { children, fallbackType = 'partial' } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      const FallBack = FALLBACK_MAP[fallbackType];
      return <FallBack />;
    }

    return children;
  }
}

interface SentryErrorBoundaryProps extends PropsWithChildren {
  fallbackType?: FallbackType;
}

export const SentryErrorBoundary = ({ fallbackType = 'partial', children }: SentryErrorBoundaryProps) => (
  <Sentry.ErrorBoundary fallback={FALLBACK_MAP[fallbackType]}>{children}</Sentry.ErrorBoundary>
);
